import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { Volume2, Pause, Loader2, Headphones } from 'lucide-react';

/** Shorthand label (no native-script suffix) for compact pills. */
const SHORT = { en: 'English', ta: 'Tamil', hi: 'Hindi', ne: 'Nepali', kn: 'Kannada', ml: 'Malayalam', bn: 'Bengali', mr: 'Marathi', te: 'Telugu' };

/**
 * In-place audio QA controls for a single SOP row.
 *
 * Renders one compact pill per ready translation (Tamil / Hindi / ...). CEO
 * and Leaders can click any pill to preview the ElevenLabs narration directly
 * in the Training Center — no assignment, no role-switching required.
 *
 * Behaviour:
 *   • First tap, cached   → instant stream from GridFS
 *   • First tap, no cache → calls ElevenLabs (~26s) then plays + caches
 *   • Tap again           → pause; tap a different lang → switches audio
 *
 * Only one player is active per row at a time.
 */
export default function SopListenPills({ sopId }) {
  const [translations, setTranslations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeLang, setActiveLang] = useState('');
  const [state, setState] = useState('idle'); // idle | loading | playing | error
  const [errorMsg, setErrorMsg] = useState('');
  const audioRef = useRef(null);

  // Lazy-init one <audio> element per row, cleaned up on unmount.
  if (!audioRef.current) audioRef.current = new Audio();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/sop-translations/by-sop/${sopId}`);
        if (alive) setTranslations(r.data || []);
      } catch (_) {
        if (alive) setTranslations([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
      try { audioRef.current?.pause(); } catch (_) { /* no-op */ }
    };
  }, [sopId]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return undefined;
    const onEnded = () => setState('idle');
    const onError = () => { setState('error'); setErrorMsg('Playback failed'); };
    a.addEventListener('ended', onEnded);
    a.addEventListener('error', onError);
    return () => {
      a.removeEventListener('ended', onEnded);
      a.removeEventListener('error', onError);
    };
  }, []);

  const play = async (t) => {
    const a = audioRef.current;
    // Clicking the active pill while playing → pause
    if (activeLang === t.lang && state === 'playing') {
      a.pause();
      setState('idle');
      return;
    }
    // Clicking a different pill → switch
    if (activeLang !== t.lang) {
      try { a.pause(); } catch (_) { /* no-op */ }
      a.src = '';
    }
    setActiveLang(t.lang);
    setState('loading');
    setErrorMsg('');
    try {
      const baseURL = api.defaults.baseURL;
      const res = await fetch(`${baseURL}/sop-translations/${t.translation_id}/audio`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      a.src = URL.createObjectURL(blob);
      await a.play();
      setState('playing');
      // Tell upstream the audio now exists (so pill stops saying "Generate")
      setTranslations((prev) => prev.map((x) => x.translation_id === t.translation_id ? { ...x, has_audio: true } : x));
    } catch (e) {
      setState('error');
      setErrorMsg(String(e?.message || e).slice(0, 30));
    }
  };

  if (loading) return null;
  if (!translations.length) return null;

  return (
    <div className="mt-1.5 flex items-center gap-1 flex-wrap" data-testid={`sop-listen-pills-${sopId}`}>
      <span className="text-[10px] inline-flex items-center gap-1" style={{ color: '#9CA3AF' }}>
        <Headphones size={10} /> QA audio:
      </span>
      {translations.map((t) => {
        const isActive = activeLang === t.lang;
        const isPlaying = isActive && state === 'playing';
        const isLoading = isActive && state === 'loading';
        const isError = isActive && state === 'error';
        const label = SHORT[t.lang] || t.lang?.toUpperCase();
        const needsGen = !t.has_audio;
        return (
          <button
            key={t.translation_id}
            type="button"
            onClick={() => play(t)}
            disabled={isLoading}
            title={needsGen ? `First play generates mp3 (~26s) — then cached forever` : `Play ${label} narration`}
            className="text-[10px] px-2 py-0.5 rounded-full border font-medium inline-flex items-center gap-1 transition disabled:opacity-60"
            style={{
              backgroundColor: isPlaying ? '#F5C86B' : isError ? '#FEF2F2' : needsGen ? '#FFF' : '#ECFDF5',
              color: isPlaying ? '#8C560F' : isError ? '#D4726A' : needsGen ? '#6B7280' : '#2D8B5E',
              borderColor: isPlaying ? '#F5C86B' : isError ? '#FECACA' : needsGen ? '#E5E1DC' : '#86EFAC',
            }}
            data-testid={`sop-listen-${sopId}-${t.lang}`}
          >
            {isLoading ? <Loader2 size={10} className="animate-spin" />
              : isPlaying ? <Pause size={10} />
              : <Volume2 size={10} />}
            {isLoading ? 'Generating…'
              : isPlaying ? `Pause ${label}`
              : isError ? `Retry ${label}`
              : needsGen ? `Generate ${label}`
              : `Listen ${label}`}
          </button>
        );
      })}
    </div>
  );
}
